import { useEffect, useRef } from "react";
import { m, AnimatePresence } from "framer-motion";

interface TimeMenuProps {
  currentFilter: "today" | "week" | "month" | null;
  onSelectFilter: (filter: "today" | "week" | "month" | null) => void;
  onClose: () => void;
  /** Where the menu opens relative to its trigger. Defaults to "up". */
  placement?: "up" | "down";
}

const TimeMenu = ({
  currentFilter,
  onSelectFilter,
  onClose,
  placement = "up",
}: TimeMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        // Check if the click is on the time button itself (to prevent immediate close)
        const target = e.target as HTMLElement;
        if (!target.closest('[aria-label="Open time filter menu"]')) {
          onClose();
        }
      }
    };

    // Add delay to prevent immediate closure when menu opens
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);
  const options = [
    { value: null, label: "All time" },
    { value: "today" as const, label: "Today" },
    { value: "week" as const, label: "Past week" },
    { value: "month" as const, label: "Past month" },
  ];

  return (
    <AnimatePresence>
      <m.div
        ref={menuRef}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`absolute left-0 w-48 bg-popover border border-border rounded-lg shadow-xl z-50 ${
          placement === "down" ? "top-full mt-2" : "bottom-full mb-2"
        }`}
      >
        <div className="p-2">
          {options.map((option) => (
            <button
              key={option.value || "all"}
              onClick={() => onSelectFilter(option.value)}
              className={`
                w-full text-left px-3 py-2 rounded-md text-sm
                hover:bg-accent transition-colors
                ${currentFilter === option.value ? "bg-blue-50 text-blue-700" : "text-foreground"}
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      </m.div>
    </AnimatePresence>
  );
};

export default TimeMenu;
